import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import ms from 'ms'
import configment from '@adc/configment'
import { hooks as coreHooks } from '@adc/ui-core'
import { trackBuyingPreferencesNudge } from '@adc/analytics'
import * as blueprint from '@adc/blueprint'
import type {
  BuyingCriteriaState,
  UseBuyingPreferencesOptions,
} from '@adc/blueprint'
import { useApplicationContext } from '../../auctionApplication/context'
import { useApplicationModals, ModalIds } from '../../auctionApplication/modals'
import ProgressiveCaptureContent from './index'
import { buyingPreferenceFlowDefinition } from './buyingPreferenceFlow'

const NUDGE_KEY = 'buying_preference_nudge'
const NUDGE_DURATIONS_CONFIG_KEY = 'NUDGE_RECURRENCE_DURATIONS'
const DISPLAY_DELAY_KEY = 'buying_preference_nudge_display_delay'

export interface UseProgressiveCaptureOptions {
  /** Current preferences state (pass null if not loaded yet) */
  preferences: BuyingCriteriaState | null
  preferencesOptions: UseBuyingPreferencesOptions
  /** Called when user clicks "Answer More" — opens the full modal */
  onAnswerMore?: () => void
  /** Number of confirmed attributes (for progress display) */
  completedCount?: number
  /** Analytics callback. */
  track?: (event: string, properties: Record<string, unknown>) => void
  /** Feature flags from the app. The hook reads `fe.resi.enable-buybox-preference-carousel`. */
  featureFlags?: Record<string, unknown>
}

export interface UseProgressiveCaptureReturn {
  /** Call after a property save. Opens progressive capture flow if eligible. */
  nudgeAfterSave: () => void
  /** Whether a nudge is eligible to show */
  shouldNudge: boolean
  /** Whether there are unanswered preference steps */
  hasUnansweredSteps: boolean
}

/**
 * useProgressiveCapture — self-contained hook for the buying preference capture flow.
 *
 * The delay before the nudge modal opens is configurable via
 * `NUDGE_RECURRENCE_DURATIONS.buying_preference_nudge_display_delay` (configment);
 * missing or invalid values open the modal immediately.
 *
 * @example
 * const { nudgeAfterSave } = useProgressiveCapture({
 *   preferences: data.criteria,
 *   preferencesOptions,
 *   onAnswerMore: () => openFullBuyingCriteriaModal(),
 * })
 * // After save:
 * nudgeAfterSave()
 */
export const useProgressiveCapture = ({
  preferences,
  preferencesOptions,
  onAnswerMore,
  completedCount = 0,
  track,
  featureFlags,
}: UseProgressiveCaptureOptions): UseProgressiveCaptureReturn => {
  const isFeatureEnabled =
    !!featureFlags?.['fe.resi.enable-buybox-preference-carousel']
  const { user } = useApplicationContext()
  const isLoggedIn = !!user?.logged_in

  const hasFetchedMilestone = useRef(false)

  const { data: captureData, actions: captureActions } =
    blueprint.user.hooks.useBuyingCriteria({
      ...preferencesOptions,
      autoSave: false,
    })

  const { data: nudgeData, actions: nudgeActions } =
    coreHooks.useWebApplicationNudges()

  // Hydrate the nudge cooldown from the backend marketing-milestones record.
  // The cooldown (active/inactive) and pristine state both live in device
  // storage, which is empty on a fresh device or after a storage clear. Seeding
  // buying_preference_last_nudged_date once on mount means a returning user is
  // neither treated as pristine nor re-nudged inside the cooldown window, even
  // on a device they've never used. hydrate() is last-write-wins, so a fresher
  // local nudge is never clobbered. Best effort: a failed fetch falls back to
  // local-only device-storage behavior.
  useEffect(() => {
    if (!isLoggedIn || hasFetchedMilestone.current) return
    hasFetchedMilestone.current = true
    blueprint.user.providers
      .getUserMarketingMilestones()
      .then(result => {
        const lastNudgedAt =
          result?.marketing_milestones?.buying_preference_last_nudged_date
        if (!lastNudgedAt) return undefined
        return nudgeActions.hydrate({
          [NUDGE_KEY]: {
            lastNudgedAt,
            // Recomputed from config inside hydrate(); ignored here.
            nextNudgeScheduledAt: lastNudgedAt,
          },
        })
      })
      .catch(() => {
        // Best effort — fall back to local device-storage state.
      })
    // nudgeActions is intentionally omitted: hydrate's identity changes when
    // storage updates, and the ref guard already makes this fetch-once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  const isNudgeActive = nudgeData.active.has(NUDGE_KEY)
  const isPristine = nudgeData.pristine?.has(NUDGE_KEY) ?? false

  const hasUnansweredSteps = useMemo(() => {
    if (isPristine) return true
    return buyingPreferenceFlowDefinition.some(
      step =>
        !step.canBeSkipped?.({
          initialData: preferences ?? undefined,
          context: { isPristine },
        })
    )
  }, [preferences, isPristine])

  const shouldNudge = useMemo(() => {
    if (!isFeatureEnabled) return false
    if (!isLoggedIn) return false
    if (!isNudgeActive) return false
    return hasUnansweredSteps
  }, [isFeatureEnabled, isLoggedIn, isNudgeActive, hasUnansweredSteps])

  const markNudged = useCallback(async () => {
    await nudgeActions.nudge(NUDGE_KEY)
    try {
      await blueprint.user.providers.setUserMarketingMilestones(
        'BUYING_PREFERENCE_NUDGE'
      )
    } catch {
      // Best effort
    }
  }, [nudgeActions])

  const nudgeEventProps = useMemo(
    () => ({ is_pristine: isPristine, completed_count: completedCount }),
    [isPristine, completedCount]
  )

  // Emit nudge analytics. By default the hook fires them itself using the
  // application-context user and @adc/analytics (which stamps pageClassification
  // so the surface — Homepage / SRP / PDP / Calendar / Dashboard — is
  // segmentable). Consumers may pass `track` to override with their own
  // per-repo context.
  const emit = useCallback(
    (event: string, props: Record<string, unknown>) =>
      track
        ? track(event, props)
        : trackBuyingPreferencesNudge(user, event, props),
    [track, user]
  )

  const closeRef = useRef<() => void>(() => {})

  const handleConfirm = useCallback(async () => {
    emit('nudge_completed', nudgeEventProps)
    await markNudged()
  }, [markNudged, emit, nudgeEventProps])

  const handleSkip = useCallback(async () => {
    emit('nudge_skipped', nudgeEventProps)
    await markNudged()
    closeRef.current()
  }, [markNudged, emit, nudgeEventProps])

  const handleAnswerMore = useCallback(() => {
    onAnswerMore?.()
  }, [onAnswerMore])

  const handleDone = useCallback(() => {
    emit('nudge_dismissed', nudgeEventProps)
    closeRef.current()
  }, [emit, nudgeEventProps])

  const modalProps = useMemo(
    () => ({
      title: '',
      size: 'tiny',
      elmId: 'buying-preference-nudge-modal',
      style: { overflow: 'visible' },
      children: (
        <ProgressiveCaptureContent
          data={captureData}
          actions={captureActions}
          onConfirm={handleConfirm}
          onSkip={handleSkip}
          onAnswerMore={handleAnswerMore}
          onDone={handleDone}
          completedCount={completedCount}
          isPristine={isPristine}
        />
      ),
    }),
    [
      captureData,
      captureActions,
      handleConfirm,
      handleSkip,
      handleAnswerMore,
      handleDone,
      completedCount,
      isPristine,
    ]
  )

  const { open, close } = useApplicationModals(ModalIds.General, modalProps)
  closeRef.current = close

  // Ref so a pending delayed open re-checks eligibility at fire time.
  const openNudgeRef = useRef<() => void>(() => {})
  openNudgeRef.current = () => {
    if (!shouldNudge) return
    emit('nudge_shown', nudgeEventProps)
    open()
  }

  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
    },
    []
  )

  const nudgeAfterSave = useCallback(() => {
    if (!shouldNudge) return
    const configuredDelay = (
      configment.get(NUDGE_DURATIONS_CONFIG_KEY, {}) as Record<string, string>
    )[DISPLAY_DELAY_KEY]
    const displayDelayMs = ms(String(configuredDelay ?? '0s')) ?? 0
    if (displayDelayMs <= 0) {
      openNudgeRef.current()
      return
    }
    if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
    delayTimerRef.current = setTimeout(() => {
      delayTimerRef.current = null
      openNudgeRef.current()
    }, displayDelayMs)
  }, [shouldNudge])

  return {
    nudgeAfterSave,
    shouldNudge,
    hasUnansweredSteps,
  }
}
